import { Select } from "@hisani/ui";
import { useTranslations } from "next-intl";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { DISTANCE_OPTIONS } from "./stopwatch-workspace-model";

export function StopwatchDistanceCombobox({
  value,
  ariaLabel,
  onChange,
}: {
  readonly value: number;
  readonly ariaLabel: string;
  readonly onChange: (meters: number) => void;
}) {
  const t = useTranslations("Stopwatch");
  const selectedOption = DISTANCE_OPTIONS.find(
    ({ meters }) => meters === value,
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const [contentCollisionBoundary, setContentCollisionBoundary] =
    useState<Element | null>(null);
  const updateCollisionBoundary = useCallback(() => {
    setContentCollisionBoundary(
      window.matchMedia?.("(min-width: 1200px)").matches === true
        ? (rootRef.current?.closest("[data-stopwatch-phone-viewport]") ?? null)
        : null,
    );
  }, []);
  const setRootElement = useCallback(
    (element: HTMLDivElement | null) => {
      rootRef.current = element;
      if (element !== null) {
        updateCollisionBoundary();
      }
    },
    [updateCollisionBoundary],
  );

  useEffect(() => {
    const desktopStory = window.matchMedia?.("(min-width: 1200px)");
    if (desktopStory === undefined) {
      return;
    }

    desktopStory.addEventListener("change", updateCollisionBoundary);
    return () =>
      desktopStory.removeEventListener("change", updateCollisionBoundary);
  }, [updateCollisionBoundary]);

  return (
    <div ref={setRootElement} className="relative">
      <Select
        value={String(value)}
        aria-label={ariaLabel}
        className="[&_[role=combobox]>span]:opacity-0 [&_[role=combobox]]:gap-1 [&_[role=combobox]]:rounded-none [&_[role=combobox]]:border-0 [&_[role=combobox]]:bg-transparent [&_[role=combobox]]:pe-1 [&_[role=combobox]]:ps-1.5 [&_[role=combobox]]:text-xs [&_[role=combobox]]:shadow-none"
        contentCollisionBoundary={contentCollisionBoundary}
        contentSide="top"
        onValueChange={(nextValue) => {
          const option = DISTANCE_OPTIONS.find(
            ({ meters }) => String(meters) === nextValue,
          );
          if (option !== undefined) onChange(option.meters);
        }}
      >
        {DISTANCE_OPTIONS.map((option) => (
          <option key={option.meters} value={String(option.meters)}>
            {t("distanceOption", option)}
          </option>
        ))}
      </Select>
      {selectedOption === undefined ? null : (
        <span
          className="pointer-events-none absolute inset-y-0 start-1.5 end-4 flex items-center overflow-hidden text-xs font-semibold whitespace-nowrap text-primary"
          aria-hidden="true"
        >
          {t("distanceOption", selectedOption)}
        </span>
      )}
    </div>
  );
}
